import { atom, selector, selectorFamily } from "recoil"

type SyncWaiting = {
  quotes: boolean
  estimates: boolean
  savings: boolean
  imagery: boolean
  incentives: boolean
  activeLoan: boolean
  design: boolean
  brochure: boolean
  proposal: boolean
}

export const syncWaitingState = atom<SyncWaiting>({
  key: `syncWaiting`,
  default: {
    quotes: false,
    estimates: false,
    savings: false,
    imagery: false,
    incentives: false,
    activeLoan: false,
    design: false,
    brochure: false,
    proposal: false,
  },
})

/**
 * Tracks whether a design save is in flight and the backend is
 * recalculating estimates/brochure. Set to true on save, cleared
 * when the brochure SSE event fires and the brochure is refetched.
 * This is separate from syncWaitingState to avoid triggering
 * premature refetches.
 */
export const designSaveRecalculatingState = atom<boolean>({
  key: `designSaveRecalculating`,
  default: false,
})

/**
 * Bumped on BrochureUpdated so useBrochure refetches even when
 * syncWaiting.brochure is already true from an in-flight empty fetch.
 */
export const brochureSyncEpochState = atom<number>({
  key: `brochureSyncEpoch`,
  default: 0,
})

/**
 * Tracks whether a utility (Monthly Bill) save queued a background savings
 * recalculation. Set from PUT .../selection when recalcQueued is true; cleared
 * only after a fresher Savings snapshot loads. Separate from syncWaitingState
 * so we do not immediately refetch-and-clear against a still-stale snapshot.
 */
export const utilitySavingsRecalculatingState = atom<boolean>({
  key: `utilitySavingsRecalculating`,
  default: false,
})

/**
 * Savings.updatedAt observed when utility savings recalculation was queued.
 * Used to detect a fresh snapshot before clearing pending.
 */
export const utilitySavingsRecalcBaselineUpdatedAtState = atom<string | null>({
  key: `utilitySavingsRecalcBaselineUpdatedAt`,
  default: null,
})

/**
 * Proposal id that queued the outstanding utility-savings recalculation.
 * Pending is ignored/cleared when the Seller navigates to a different Proposal.
 */
export const utilitySavingsRecalcProposalIdState = atom<string | null>({
  key: `utilitySavingsRecalcProposalId`,
  default: null,
})

export const brochureValuesLoadingState = selector<boolean>({
  key: `brochureValuesLoading`,
  get: ({ get }) => {
    return get(designSaveRecalculatingState) || get(syncWaitingState).brochure
  },
})

/** True while utility recalc is queued or SSE-driven savings sync is in flight. */
export const utilitySavingsPendingState = selector<boolean>({
  key: `utilitySavingsPending`,
  get: ({ get }) => {
    return get(utilitySavingsRecalculatingState) || get(syncWaitingState).savings
  },
})

export const syncWaitingForEntityState = selectorFamily<
  boolean,
  keyof SyncWaiting
>({
  key: `syncWaitingForEntity`,
  get:
    (entity: keyof SyncWaiting) =>
    ({ get }) => {
      return get(syncWaitingState)[entity]
    },
  set:
    (entity: keyof SyncWaiting) =>
    ({ set }, newValue) => {
      set(syncWaitingState, (current) => ({
        ...current,
        [entity]: newValue,
      }))
    },
})
